import React, { useCallback, useEffect, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { useContainerContext } from '@nextroll/ar-container-context';

import { IPropertyError, IWorkflowActionConfig, NodeDropDownSectionValue, ActionMetadata, ActionConfigValues, NodeDropDownSectionProps, NodeDropDownSectionDataFcn } from 'types/rofl';
import { ActionPropertyValuesInput, ComponentPropertyInputConfig } from 'types/propertyGeneration';
import { WorkflowAction, InputProperty } from 'types/roflWorkflowActions';
import { INodeBasePanelElementConfig } from 'components/basic/NodeBase/NodeBase';
import { getWorkflowActionPropertyValues } from 'api/workflowActionsApi';
import { NodeDropDownSection, defaultDropDownSearch } from 'components/basic/inputProperty/NodeDropDownSection/NodeDropDownSection';
import useAbortController from 'hooks/useAbortController';
import { UseRequest, RequestError } from 'hooks/useRequest';
import { generateInitialMetadata } from 'store/nodeSlice';
import { generatePropertyErrorKey } from 'util/errors';
import { ActionPropertyValuesResult, Lookup, LookupType, RecordField, PropertyType } from 'types/roflWorkflowActionProperty';
import { isFieldDefinitionRequired } from 'util/util';

/**
 * GenerateNodePanelDropDownHookConfig are the config for the useGenerateNodePanelDropDownConfig hook
 */
export interface GenerateNodePanelDropDownHookConfig {
    serviceTitle: string | undefined;
    workflowAction: WorkflowAction | undefined;
    call: UseRequest[0];
    propertyErrors: Record<string, IPropertyError> | undefined;
    authedWithoutMissingScopes: boolean; // is this node authed correctly without any missing scopes
    handleRequestError: (requestError: RequestError) => boolean;
    fieldInitialPropertyTextGenerated: Record<string, boolean>;
    setFieldInitialPropertyTextGenerated: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

/**
 * GenerateNodePanelDropDownConfig are the config for the generateNodePanelDropDownConfig function
 */

type TextForNodeType = ((config: IWorkflowActionConfig, abortSignal: AbortSignal) => Promise<string>);
interface GenerateNodePanelDropDownConfig {
    propertyName: string;
    parentPropertyName?: string;
    displayName?: string;
    lookup?: Lookup;
    enum?: ActionPropertyValuesResult[];
    isDropdownDisabled?: boolean;
    actionPropertyValuesInput?: ActionPropertyValuesInput;
    required: boolean;
    actionMetadata?: ActionMetadata;
    workflowConfig: ActionConfigValues;
    handleUpdateWorkflowNode: (config: ActionConfigValues, metadata?: ActionMetadata) => void;
    multivalued?: boolean;
    getTextForNode?: TextForNodeType;
    nodeDropDownProps?: Partial<NodeDropDownSectionProps>;
    suppressSearch?: boolean;
    propertyType?: PropertyType;
}

/**
 * useGenerateNodePanelDropDownConfig returns a function to generate the config for the node dropdown property input
 */
function useGenerateNodePanelDropDownConfig({
    serviceTitle,
    workflowAction,
    call,
    propertyErrors,
    authedWithoutMissingScopes,
    handleRequestError,
    fieldInitialPropertyTextGenerated,
    setFieldInitialPropertyTextGenerated
}: GenerateNodePanelDropDownHookConfig) {
    /**
     *  generateNodePanelDropDown dynamically generates node panel dropdowns from the passed in config
     */

    const getSignal = useAbortController();
    const { advertisable: { eid: advertisableEid = '' } = {} } = useContainerContext();
    // nodePanelDropDownIdentifierValid holds the association of whether or not a field (by identifier is valid or not)
    const [
        nodePanelDropDownIdentifierValid,
        setNodePanelDropDownIdentifierValid
    ] = useState<Record<string, boolean>>({});

    // cleanup
    useEffect(() => {
        setNodePanelDropDownIdentifierValid({});
    }, [workflowAction]);

    const generateNodePanelDropDownConfig
        = useCallback((config: GenerateNodePanelDropDownConfig): INodeBasePanelElementConfig => {
            const fieldKey = `workload-service-node-panel-element-${config.propertyName}`;
            /**
            * data is called when the node drop down is opened.
            * @returns optionValues
            */
            let data: NodeDropDownSectionDataFcn;
            let allowRefresh: boolean = false;
            /**
             *  onSelect is called when a node drop down option is selected.
             *  It updates the stored workflow value and the metadata
             */
            let onSelect: NodeDropDownSectionProps['onSelect'];
            let onSearch: NodeDropDownSectionProps['onSearch'];
            let getTextForNode: TextForNodeType | undefined;
            let actionPropertyValuesInput: ActionPropertyValuesInput = {};

            // default onSearch function
            onSearch = defaultDropDownSearch;

            if (config.lookup?.type === LookupType.OPTIONS) {
                actionPropertyValuesInput = config.actionPropertyValuesInput || { record_field: config.propertyName };

                data = async getDataConfig => {
                    if (config.isDropdownDisabled) {
                        return [];
                    }
                    return getWorkflowActionPropertyValues({
                        advertisableEid,
                        workflowActionEid: workflowAction?.eid || '',
                        propertyName: config.parentPropertyName || config.propertyName,
                        call,
                        signal: getSignal(),
                        onError: (requestError: RequestError) => (
                            // handle global ActionNode errors first, and if there is nothing
                            // handle any specific to the field
                            handleRequestError(requestError) || !!getDataConfig?.onError?.(requestError)
                        ),
                        actionPropertyValuesInput: {
                            ...actionPropertyValuesInput,
                            search: getDataConfig?.searchParam || undefined
                        }
                    });
                };

                onSelect = ({ value, display_value } : NodeDropDownSectionValue) => {
                    setFieldInitialPropertyTextGenerated(
                        (currentFieldInitialPropertyTextGenerated: Record<string, boolean>) => {
                            return { ...currentFieldInitialPropertyTextGenerated, [fieldKey]: true };
                        }
                    );
                    const metadata = config.actionMetadata || generateInitialMetadata();
                    const actionMetadataProperty = {
                        lookup: {
                            type: LookupType.OPTIONS,
                            options_lookup: {
                                selected: {
                                    display_value,
                                }
                            }
                        }
                    };
                    config.handleUpdateWorkflowNode({
                        ...config.workflowConfig, [config.propertyName]: value
                    }, {
                        ...metadata,
                        properties: {
                            ...metadata.properties, [config.propertyName]: actionMetadataProperty
                        }
                    });
                };
                allowRefresh = true;
                getTextForNode = async workflowActionConfig => {
                    let parentNodeText = '';
                    if (fieldInitialPropertyTextGenerated[fieldKey]) {
                        parentNodeText = workflowActionConfig.metadata?.properties?.[config.propertyName]?.lookup?.options_lookup?.selected?.display_value || '';
                    } else {
                        setFieldInitialPropertyTextGenerated(
                            (currentFieldInitialPropertyTextGenerated: Record<string, boolean>) => {
                                return { ...currentFieldInitialPropertyTextGenerated, [fieldKey]: true };
                            }
                        );

                        // if this field has an error don't look it up
                        if (authedWithoutMissingScopes && !propertyErrors?.[config.propertyName]) {
                            const propertyValues = await data();
                            const nodeConfigValue = workflowActionConfig.config?.[config.propertyName];
                            const selectedNodeValue = (propertyValues || []).find(
                                (option: { value: string | number | undefined }) => option.value === nodeConfigValue
                            );
                            parentNodeText = selectedNodeValue?.display_value || '';
                        }
                    }
                    return parentNodeText;
                };
            } else if (config.enum) {
                const inputPropertyEnum = config.enum;
                allowRefresh = false;
                onSelect = ({ value }: NodeDropDownSectionValue, _isOptionSelected, selectedOptions) => {
                    // Multi-select mode
                    if (selectedOptions) {
                        config.handleUpdateWorkflowNode({
                            ...config.workflowConfig,
                            [config.propertyName]: selectedOptions.map(selectedOption => selectedOption.value)
                        });
                    } else {
                        config.handleUpdateWorkflowNode({
                            ...config.workflowConfig, [config.propertyName]: value
                        });
                    }
                };

                /**
                 *  data converts enum values to node dropdown options
                 */
                data = async () => (
                    inputPropertyEnum.map(
                        propEnum => ({ display_value: propEnum.display_value, value: propEnum.value })
                    )
                );
                getTextForNode = async workflowActionConfig => {
                    const propertyValues = await data();
                    const nodeConfigValue = workflowActionConfig.config?.[config.propertyName];
                    const selectedNodeValue = propertyValues
                        .find(
                            (option: { value: string | number | undefined }) => option.value === nodeConfigValue
                        );
                    return selectedNodeValue?.display_value || '';
                };
            } else if (config.propertyType === PropertyType.BOOL) {
                data = async () => {
                    return [
                        {
                            display_value: 'Yes',
                            value: true
                        },
                        {
                            display_value: 'No',
                            value: false
                        }
                    ] as ActionPropertyValuesResult[];
                };
                onSearch = undefined;
            }
            return {
                key: `workload-service-node-panel-element-${config.propertyName}`,
                component: () => (
                    <NodeDropDownSection
                        identifier={fieldKey}
                        setIsValid={setNodePanelDropDownIdentifierValid}
                        selected={config.nodeDropDownProps?.selected}
                        selectedDisplayValue={config.nodeDropDownProps?.selectedDisplayValue}
                        onSelect={config.nodeDropDownProps?.onSelect || onSelect}
                        serviceTitle={serviceTitle || ''}
                        entityTitle={config.displayName || ''}
                        data={data}
                        actionPropertyValuesInput={actionPropertyValuesInput}
                        propertyError={config.nodeDropDownProps?.propertyError}
                        allowRefresh={allowRefresh}
                        multiple={config.multivalued}
                        placeholder={config.propertyName === 'record_type' ? i18n.gettext('Select Record Type') : i18n.gettext('Select Value')}
                        onSearch={onSearch}
                        isDisabled={config.isDropdownDisabled}
                        shouldSearchBackend={config.lookup?.options_lookup?.supports_search}
                        hideLabel={!!config.nodeDropDownProps?.hideLabel}
                    />
                ),
                isRequired: config.required,
                validate: () => nodePanelDropDownIdentifierValid[fieldKey],
                getTextForNode: config.getTextForNode || getTextForNode,
            };
        }, [advertisableEid,
            workflowAction?.eid,
            call,
            getSignal,
            handleRequestError,
            setFieldInitialPropertyTextGenerated,
            fieldInitialPropertyTextGenerated,
            authedWithoutMissingScopes,
            propertyErrors,
            serviceTitle,
            nodePanelDropDownIdentifierValid]);

    /**
     *  isNodeDropDownConfig returns whether or not the inputProperty will be handled by this generator
     */
    const isNodeDropDownConfig = useCallback((inputProperty: InputProperty): boolean => {
        return inputProperty.lookup?.type === LookupType.OPTIONS || !!inputProperty.enum;
    }, []);

    const generateNodePanelDropDownConfigFromInputProperty
        = useCallback((inputConfig: ComponentPropertyInputConfig): INodeBasePanelElementConfig => {
            const config = {
                propertyName: inputConfig.inputProperty.name,
                displayName: inputConfig.inputProperty.display_name,
                lookup: inputConfig.inputProperty.lookup,
                enum: inputConfig.inputProperty.enum,
                required: inputConfig.inputProperty.required,
                actionMetadata: inputConfig.actionMetadata,
                workflowConfig: inputConfig.workflowConfig,
                handleUpdateWorkflowNode: inputConfig.handleUpdateWorkflowNode,
                nodeDropDownProps: {
                    selected: inputConfig.workflowConfig?.[inputConfig.inputProperty.name],
                    propertyError: propertyErrors?.[generatePropertyErrorKey(inputConfig.inputProperty.name)],
                    hideLabel: false,
                },
                propertyType: inputConfig.inputProperty.type
            };
            return generateNodePanelDropDownConfig(config);
        }, [generateNodePanelDropDownConfig, propertyErrors]);

    const generateNodePanelDropDownConfigFromRecordField
        = useCallback((
            inputConfig: ComponentPropertyInputConfig,
            field: RecordField,
            isDropdownDisabled: boolean,
            actionPropertyValuesInput: ActionPropertyValuesInput,
            overrides:{
                nodeDropDownProps: Partial<NodeDropDownSectionProps>;
                getTextForNode: TextForNodeType | undefined;
            },
        ): INodeBasePanelElementConfig => {
            const config = {
                propertyName: field.name,
                parentPropertyName: inputConfig.inputProperty.name,
                displayName: field.display_name,
                lookup: field.lookup,
                enum: field.enum,
                isDropdownDisabled,
                actionPropertyValuesInput,
                required: isFieldDefinitionRequired(field, inputConfig.inputProperty.record_operation),
                multivalued: field.multivalued,
                actionMetadata: inputConfig.actionMetadata,
                workflowConfig: inputConfig.workflowConfig,
                handleUpdateWorkflowNode: inputConfig.handleUpdateWorkflowNode,
                getTextForNode: overrides.getTextForNode,
                nodeDropDownProps: overrides.nodeDropDownProps,
                propertyType: field.type
            };

            return generateNodePanelDropDownConfig(config);
        }, [generateNodePanelDropDownConfig]);

    return {
        generateNodePanelDropDownConfigFromInputProperty,
        generateNodePanelDropDownConfigFromRecordField,
        isNodeDropDownConfig
    };
}

export default useGenerateNodePanelDropDownConfig;
